import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { useQcChecklistsQuery } from '../../qc/useQc.js';

import { HOME_CARD_LINK, HOME_CARD_ROW, HomeCard, homeCardStatus } from './HomeCard.js';

/** Five is what fits beside the week card without the section growing its own scrollbar. */
const MAX_ROWS = 5;

/**
 * "My checklists" — the quality-control work assigned to the signed-in crew member.
 *
 * `?mine=1` is the api's own self-scoping filter, so the SPA sends no employee id and can show one
 * person their own list without holding an employee id it does not have (`GET /api/v1/me` returns
 * none).
 *
 * An approved checklist is finished, so it is dropped here. This card answers "what is still on
 * me", and a list that keeps closed work in it stops answering that within a week. The count that
 * decides the empty state is therefore the OPEN one, not the response length: a week where every
 * checklist was signed off reads as "nothing on you", which is true.
 */
export function CrewChecklists() {
  const { t } = useTranslation();
  const query = useQcChecklistsQuery({ mine: true });

  const open = (query.data?.checklists ?? []).filter(
    (checklist) => checklist.status !== 'approved',
  );
  const shown = open.slice(0, MAX_ROWS);

  return (
    <HomeCard
      headingId="crew-qc-heading"
      title={t('home:crew.checklists.heading')}
      action={
        <Link to="/qc" className={HOME_CARD_LINK}>
          {t('home:crew.checklists.openAll')}
        </Link>
      }
      status={homeCardStatus(query, shown.length)}
      errorText={t('home:crew.checklists.unavailable')}
      emptyText={t('home:crew.checklists.empty')}
      onRetry={() => void query.refetch()}
    >
      <ul className="mt-3 flex flex-col gap-2">
        {shown.map((checklist) => (
          <li key={checklist.id}>
            <Link
              to="/qc/$id"
              params={{ id: String(checklist.id) }}
              className={`flex min-h-11 flex-col justify-center hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${HOME_CARD_ROW}`}
            >
              <span className="font-medium text-foreground text-sm">{checklist.name}</span>
              <span className="text-muted-foreground text-xs">
                {checklist.jobName ? `${checklist.jobName} · ` : ''}
                {t('home:crew.checklists.answered', {
                  answered: checklist.answeredCount,
                  total: checklist.itemCount,
                })}
                {/*
                  TEXT, never colour alone (A11Y-01). An outstanding item is a fail or a
                  needs-review that will block sign-off, so it says so in words.
                */}
                {checklist.outstandingCount > 0 ? (
                  <span className="ml-1 font-medium text-destructive">
                    {t('home:crew.checklists.outstanding', {
                      count: checklist.outstandingCount,
                    })}
                  </span>
                ) : null}
              </span>
            </Link>
          </li>
        ))}
        {open.length > shown.length ? (
          <li className="text-muted-foreground text-xs">
            {t('home:crew.checklists.more', { count: open.length - shown.length })}
          </li>
        ) : null}
      </ul>
    </HomeCard>
  );
}
